import { useMemo } from "react"
import { useLocation } from "react-router-dom"
import { useIsMobile } from "@/hooks/useIsMobile"
import { getAssetUrl } from "@/utils/assets"

const DASHBOARD_ROUTES = [/^\/$/]

const ALT_BACKGROUND_ROUTES = [
  /^\/learn-and-practice/,
  /^\/connect/,
  /^\/my-rituality/,
  /^\/search/,
]

const CREATE_EDIT_ROUTES = /^\/(rituals|communities|coaching-sessions)\/(create|edit)/

const MAIN_NAV_ROUTES = ["/", "/learn-and-practice", "/chats", "/connect", "/my-rituality"]

const CONNECT_CATALOG_ROUTES = [
  "/connect/experts",
  "/connect/ritualists",
  "/connect/members",
  "/learn-and-practice/intentions",
]

interface RouteTypeResult {
  currentPath: string
  isDashboardRoute: boolean
  isAltRoute: boolean
  showBackground: boolean
  backgroundImage: string
  isMyRitualityRoute: boolean
  isChatsRoute: boolean
  isSettingsRoute: boolean
  isMainNav: boolean
  isCreateOrEditRoute: boolean
  isRitualDetail: boolean
  isCommunityDetail: boolean
  isProfilePage: boolean
  isOtherProfilePage: boolean
  isEditProfilePage: boolean
  isIntentionDetail: boolean
  communityId: string | undefined
  ritualId: string | undefined
  otherProfileUserId: string | undefined
}

export function useRouteType(): RouteTypeResult {
  const location = useLocation()
  const currentPath = location.pathname
  const isMobile = useIsMobile()

  const isDashboardRoute = useMemo(
    () => DASHBOARD_ROUTES.some(regex => regex.test(currentPath)),
    [currentPath],
  )

  const isAltRoute = useMemo(
    () => ALT_BACKGROUND_ROUTES.some(regex => regex.test(currentPath)),
    [currentPath],
  )

  const isCreateOrEditRoute = CREATE_EDIT_ROUTES.test(currentPath)

  const showBackground = isDashboardRoute || isAltRoute || isCreateOrEditRoute

  const isSearchRoute = currentPath.startsWith("/search")

  const isMobileBgRoute = useMemo(
    () =>
      isMobile &&
      !isSearchRoute &&
      (currentPath.startsWith("/learn-and-practice") || currentPath.startsWith("/connect")),
    [isMobile, isSearchRoute, currentPath],
  )

  const isMyRitualityMobileRoute = isMobile && currentPath === "/my-rituality"

  const backgroundImage = useMemo(
    () =>
      isMobile && isSearchRoute
        ? ""
        : isMobile && isDashboardRoute
          ? getAssetUrl("mobile_home_bg.svg")
          : isMobileBgRoute
            ? getAssetUrl("mobile_bg.png")
            : isMyRitualityMobileRoute
              ? getAssetUrl("mobile_myrituality_bg.png")
              : isDashboardRoute
                ? getAssetUrl("background_image.jpg")
                : isCreateOrEditRoute
                  ? getAssetUrl("ritual-bg-image.webp")
                  : getAssetUrl("app-background.png"),
    [
      isMobile,
      isSearchRoute,
      isDashboardRoute,
      isMobileBgRoute,
      isMyRitualityMobileRoute,
      isCreateOrEditRoute,
    ],
  )

  const isMyRitualityRoute =
    currentPath === "/my-rituality" || currentPath.startsWith("/my-rituality/")
  const isChatsRoute = currentPath === "/chats" || currentPath.startsWith("/chats/")
  const isSettingsRoute = currentPath === "/settings" || currentPath.startsWith("/settings")
  const isIntentionDetail =
    currentPath === "/learn-and-practice/intention" ||
    currentPath.startsWith("/learn-and-practice/intention") ||
    currentPath === "/learn-and-practice/intentions" ||
    currentPath.startsWith("/learn-and-practice/intentions")

  const isMainNav = useMemo(
    () =>
      MAIN_NAV_ROUTES.some(route =>
        route === "/" ? currentPath === "/" : currentPath.startsWith(route),
      ) &&
      !CONNECT_CATALOG_ROUTES.includes(currentPath) &&
      !isIntentionDetail,
    [currentPath, isIntentionDetail],
  )

  const isRitualDetail = /^\/rituals\/[^/]+$/.test(currentPath) && !isCreateOrEditRoute
  const isCommunityDetail = /^\/communities\/[^/]+$/.test(currentPath) && !isCreateOrEditRoute
  const isProfilePage = currentPath === "/profile"
  const isOtherProfilePage =
    /^\/profile\/[^/]+$/.test(currentPath) && currentPath !== "/profile/edit"
  const isEditProfilePage = currentPath === "/profile/edit"

  const communityId = isCommunityDetail ? currentPath.split("/")[2] : undefined
  const ritualId = isRitualDetail ? currentPath.split("/")[2] : undefined
  const otherProfileUserId = isOtherProfilePage ? currentPath.split("/")[2] : undefined

  return {
    currentPath,
    isDashboardRoute,
    isAltRoute,
    showBackground,
    backgroundImage,
    isMyRitualityRoute,
    isChatsRoute,
    isSettingsRoute,
    isMainNav,
    isCreateOrEditRoute,
    isRitualDetail,
    isCommunityDetail,
    isProfilePage,
    isOtherProfilePage,
    isEditProfilePage,
    isIntentionDetail,
    communityId,
    ritualId,
    otherProfileUserId,
  }
}
